import React, { useEffect, useState } from "react";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { Attendee, EventTicketTier } from "@prisma/client";
import { useSession } from "next-auth/react";
import RSVPText from "~/components/themed/RSVPText";
import { Button } from "../themed/Button";
import { User } from "next-auth";
import {
    EVENT_RSVP_STATUS,
    determineEventStatus,
    renderAskToJoin,
    renderAttending,
    renderAwaitingTicketTierSelection,
    renderFull,
    renderHosting,
    renderJoinWaitlist,
    renderLoading,
    renderLoginPrompt,
    renderMembersOnlyTagGated,
    renderMembersOnly,
    renderOnWaitlist,
    renderOtherStatus,
    renderPending,
    renderTokenGated,
} from "../events-components/EventStatusComponents";
import { If } from "../Conditional";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { getWalletNFTs } from "~/utils/web3";

type Props = {
    event: EventBySlugOutput;
    attendee: Attendee | null | undefined;
    handleRegistration: () => void;
    spotsLeft: number;
    isLoading: boolean;
    setSelectedTier: (selectedTier: EventTicketTier | null) => void;
    setDiscountCode: (discountCode: string) => void;
};

export const EventRSVPButton = ({
    event,
    attendee,
    handleRegistration,
    spotsLeft,
    isLoading,
    setSelectedTier,
    setDiscountCode,
}: Props) => {
    const { data: session, status: sessionStatus } = useSession();
    const { connection } = useConnection();
    const wallet = useWallet();

    const [hasNFT, setHasNFT] = useState(false);
    const [eventStatus, setEventStatus] = useState(EVENT_RSVP_STATUS.LOADING);

    useEffect(() => {
        if (!wallet.connected || !wallet.publicKey) return;

        const fetchNFTs = async () => {
            const userNFTs = await getWalletNFTs(wallet.publicKey!.toString());
            const matchingNFT = userNFTs.find(
                (nft) => nft.collection === event.nftTokenGateCollectionId,
            );
            setHasNFT(!!matchingNFT);
        };
        fetchNFTs().catch(console.error);
    }, [wallet]);

    const isUserHost = checkIfUserIsHost(event, session?.user);

    useEffect(() => {
        const status = determineEventStatus(
            event,
            attendee,
            isUserHost,
            spotsLeft,
            isLoading,
            sessionStatus,
            hasNFT,
        );
        setEventStatus(status);
    }, [hasNFT, sessionStatus, isLoading]);

    // leave this here for debugging the hell hole that is all the state
    // permutations here LOL
    // console.log("EVENT STATUS", { status: EVENT_RSVP_STATUS[eventStatus] });

    const handledStatuses = [
        EVENT_RSVP_STATUS.ASK_TO_JOIN,
        EVENT_RSVP_STATUS.AWAITING_TICKET_TIER_SELECTION,
        EVENT_RSVP_STATUS.JOIN_WAITLIST,
        EVENT_RSVP_STATUS.MEMBERS_ONLY_TAG_GATED,
        EVENT_RSVP_STATUS.MEMBERS_ONLY,
        EVENT_RSVP_STATUS.TOKEN_GATED,
        EVENT_RSVP_STATUS.LOADING,
        EVENT_RSVP_STATUS.LOGIN_TO_RSVP,
        EVENT_RSVP_STATUS.HOSTING,
        EVENT_RSVP_STATUS.ON_WAITLIST,
        EVENT_RSVP_STATUS.FULL,
        EVENT_RSVP_STATUS.ATTENDING,
        EVENT_RSVP_STATUS.OTHER_STATUS,
        EVENT_RSVP_STATUS.PENDING_APPROVAL,
    ];

    return (
        <>
            <If condition={eventStatus === EVENT_RSVP_STATUS.ASK_TO_JOIN}>
                {renderAskToJoin(handleRegistration)}
            </If>
            <If
                condition={
                    eventStatus ===
                    EVENT_RSVP_STATUS.AWAITING_TICKET_TIER_SELECTION
                }
            >
                {renderAwaitingTicketTierSelection({
                    event,
                    handleRegistration,
                    setSelectedTier,
                    setDiscountCode,
                })}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.JOIN_WAITLIST}>
                {renderJoinWaitlist(handleRegistration)}
            </If>

            <If
                condition={
                    eventStatus === EVENT_RSVP_STATUS.MEMBERS_ONLY_TAG_GATED
                }
            >
                {renderMembersOnlyTagGated(event)}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.MEMBERS_ONLY}>
                {renderMembersOnly()}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.TOKEN_GATED}>
                {renderTokenGated()}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.LOADING}>
                {renderLoading()}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.LOGIN_TO_RSVP}>
                {renderLoginPrompt(event)}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.HOSTING}>
                {renderHosting()}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.ON_WAITLIST}>
                {renderOnWaitlist()}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.FULL}>
                {renderFull(event)}
            </If>

            <If condition={eventStatus === EVENT_RSVP_STATUS.ATTENDING}>
                {renderAttending(attendee)}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.OTHER_STATUS}>
                {renderOtherStatus(attendee)}
            </If>
            <If condition={eventStatus === EVENT_RSVP_STATUS.PENDING_APPROVAL}>
                {renderPending()}
            </If>
            <If
                condition={
                    eventStatus === EVENT_RSVP_STATUS.INVITED ||
                    !handledStatuses.includes(eventStatus)
                }
            >
                <Button
                    className="event-rsvp-button w-full"
                    onClick={handleRegistration}
                    disabled={eventStatus === EVENT_RSVP_STATUS.DISABLED}
                >
                    <RSVPText attendee={attendee} event={event} />
                </Button>
            </If>
        </>
    );
};

// Check if the user is the host of the event
export const checkIfUserIsHost = (
    event: EventBySlugOutput,
    user: User | undefined,
) => {
    if (!user) {
        return false;
    }

    const primaryHostId = event?.hosts[0]?.user.id;

    if (!primaryHostId) {
        return false;
    }

    return (
        event?.hosts.some((host) => host.user.id === user?.id) ||
        primaryHostId === user?.id
    );
};
